/**
 * TORA — nucleo monetario bimonetario (ARS / USD).
 *
 * REGLA UNICA DEL SISTEMA
 * ----------------------
 * Todo importe es una tripleta `(importe, moneda, cotizacion)` y persiste ademas
 * su expresion en ambas monedas (`importe_ars`, `importe_usd`).
 *
 * La cotizacion se CONGELA en la fila al momento del asiento y no se recalcula
 * nunca. Cualquier reporte puede mostrarse en cualquiera de las dos monedas sin
 * volver a buscar cotizaciones ni reinterpretar la historia.
 *
 * Unica excepcion: la cuenta corriente en modo USD, donde el ancla es el
 * `importe_usd` guardado y la presentacion en pesos usa la cotizacion del dia.
 *
 * Este modulo es la FUENTE UNICA de la aritmetica de dinero del frontend y del
 * backend. Las funciones SQL `a_ars()` / `a_usd()` / `redondear_precio()` en
 * `supabase/migrations/0001_moneda.sql` replican exactamente esta semantica; si
 * cambia una, tiene que cambiar la otra (lo cubre `money.test.ts`).
 */

// ─────────────────────────────────────────────────────────────────────────────
// Tipos
// ─────────────────────────────────────────────────────────────────────────────

export const MONEDAS = ["ARS", "USD"] as const;
export type Moneda = (typeof MONEDAS)[number];

export function esMoneda(v: unknown): v is Moneda {
  return typeof v === "string" && (MONEDAS as readonly string[]).includes(v);
}

/** La otra moneda. Util para armar switches y toggles. */
export function monedaOpuesta(m: Moneda): Moneda {
  return m === "ARS" ? "USD" : "ARS";
}

/** Un importe con su moneda. La unidad minima que viaja por el sistema. */
export interface Importe {
  monto: number;
  moneda: Moneda;
}

/**
 * Un importe ya expresado en ambas monedas, con la cotizacion que se uso.
 * Es la forma en la que TODO movimiento de plata se persiste.
 */
export interface ImporteBimonetario {
  monto: number;
  moneda: Moneda;
  cotizacion: number;
  montoArs: number;
  montoUsd: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Escalas y redondeo
// ─────────────────────────────────────────────────────────────────────────────

/** Decimales con los que se persiste un importe (numeric(14,2) en Postgres). */
export const DECIMALES_IMPORTE = 2;
/** Decimales de una cotizacion (numeric(14,4) en Postgres). */
export const DECIMALES_COTIZACION = 4;

/**
 * Redondeo half-away-from-zero a `decimales`.
 *
 * `Math.round` en JS es half-up (hacia +Infinito), asi que -0.5 da -0 en vez de
 * -1: rompe la simetria entre un DEBE y un HABER del mismo valor. Postgres
 * redondea half-away-from-zero, y esta funcion tiene que coincidir con Postgres
 * o los totales calculados en el cliente no cierran contra los de la base.
 *
 * El `Number(...toFixed())` intermedio corrige el error de representacion de
 * punto flotante que hace que `1.005 * 100` sea `100.49999999999999`.
 */
export function redondear(valor: number, decimales = DECIMALES_IMPORTE): number {
  if (!Number.isFinite(valor)) {
    throw new RangeError(`redondear: valor no finito (${valor})`);
  }
  const factor = 10 ** decimales;
  const escalado = Number((valor * factor).toFixed(6));
  const redondeado = escalado < 0 ? -Math.round(-escalado) : Math.round(escalado);
  // `+ 0` normaliza -0 a 0: un saldo cero no debe mostrarse como "-$ 0,00".
  return redondeado / factor + 0;
}

/** Redondea a la escala de una cotizacion (4 decimales). */
export function redondearCotizacion(valor: number): number {
  return redondear(valor, DECIMALES_COTIZACION);
}

// ─────────────────────────────────────────────────────────────────────────────
// Conversion
// ─────────────────────────────────────────────────────────────────────────────

/** Cotizacion invalida: el resto del modulo asume que ya paso por aca. */
function validarCotizacion(cotizacion: number): void {
  if (!Number.isFinite(cotizacion) || cotizacion <= 0) {
    throw new RangeError(
      `Cotizacion invalida (${cotizacion}). Debe ser un numero finito mayor a cero.`,
    );
  }
}

/**
 * Expresa un importe en pesos.
 * `cotizacion` es siempre ARS por 1 USD (ej: 1200 = un dolar vale $1.200).
 */
export function aArs(monto: number, moneda: Moneda, cotizacion: number): number {
  if (moneda === "ARS") return redondear(monto);
  validarCotizacion(cotizacion);
  return redondear(monto * cotizacion);
}

/** Expresa un importe en dolares. */
export function aUsd(monto: number, moneda: Moneda, cotizacion: number): number {
  if (moneda === "USD") return redondear(monto);
  validarCotizacion(cotizacion);
  return redondear(monto / cotizacion);
}

/** Convierte entre monedas arbitrarias. Si son iguales, solo redondea. */
export function convertir(monto: number, desde: Moneda, hacia: Moneda, cotizacion: number): number {
  if (desde === hacia) return redondear(monto);
  return hacia === "ARS" ? aArs(monto, desde, cotizacion) : aUsd(monto, desde, cotizacion);
}

/**
 * Construye la representacion persistible de un importe: las dos monedas mas la
 * cotizacion congelada. Es lo que se escribe en toda tabla que mueva plata.
 */
export function bimonetario(monto: number, moneda: Moneda, cotizacion: number): ImporteBimonetario {
  validarCotizacion(cotizacion);
  return {
    monto: redondear(monto),
    moneda,
    cotizacion: redondearCotizacion(cotizacion),
    montoArs: aArs(monto, moneda, cotizacion),
    montoUsd: aUsd(monto, moneda, cotizacion),
  };
}

/** Lee un bimonetario persistido en la moneda pedida. Sin recalcular nada. */
export function leerEn(importe: ImporteBimonetario, moneda: Moneda): number {
  return moneda === "ARS" ? importe.montoArs : importe.montoUsd;
}

// ─────────────────────────────────────────────────────────────────────────────
// Motor de precios: markup y redondeo comercial
// ─────────────────────────────────────────────────────────────────────────────

export const MARKUP_TIPOS = ["PORCENTAJE", "FIJO"] as const;
export type MarkupTipo = (typeof MARKUP_TIPOS)[number];

export const REDONDEOS = [
  "NINGUNO",
  "UNIDAD",
  "CIEN",
  "QUINIENTOS",
  "MIL",
  "TERMINACION_990",
] as const;
export type Redondeo = (typeof REDONDEOS)[number];

/**
 * Redondeo comercial del precio de venta.
 *
 * Siempre hacia ARRIBA: un precio nunca se redondea a la baja sola, porque eso
 * come margen sin que nadie lo decida.
 *
 * - `UNIDAD`: al proximo entero (350,01 -> 351; 349,98 -> 350). Es el que cierra
 *   la vuelta de un precio escrito (ver `despejarPrecio`), en pesos y en dolares.
 * - `CIEN` / `QUINIENTOS` / `MIL`: al proximo multiplo (1.234 -> 1.300 / 1.500 / 2.000).
 * - `TERMINACION_990`: al proximo x.990 (1.234 -> 1.990; 2.100 -> 2.990).
 *   Solo tiene sentido en ARS; en USD un "990" no es una terminacion comercial.
 */
export function aplicarRedondeo(precio: number, modo: Redondeo): number {
  if (modo === "NINGUNO" || precio <= 0) return redondear(precio);

  // A centavos antes de subir: un 350 que llega como 350,0000001 no es 351.
  if (modo === "UNIDAD") return redondear(Math.ceil(redondear(precio)));

  if (modo === "TERMINACION_990") {
    const base = Math.ceil((precio - 990) / 1000) * 1000;
    return redondear(Math.max(base, 0) + 990);
  }

  const paso = modo === "CIEN" ? 100 : modo === "QUINIENTOS" ? 500 : 1000;
  return redondear(Math.ceil(precio / paso) * paso);
}

/**
 * El camino inverso del motor: de un precio de venta deseado al markup que lo
 * produce.
 *
 * EL CASO QUE LO JUSTIFICA
 * ------------------------
 * Mudarse con margenes inciertos. Se sabe a cuanto se vende una funda —lo dice
 * la lista vieja— pero no cuanto se gana. Escribir el precio a mano resolvia el
 * relevamiento y rompia el futuro: ese producto quedaba congelado y no se movia
 * nunca mas, ni cuando subia el costo ni cuando se movia el dolar.
 *
 * Despejando el markup, el precio tipeado se guarda como REGLA en vez de como
 * numero, y el producto sigue vivo.
 *
 * Devuelve null cuando no hay de donde despejar: sin costo no existe un
 * porcentaje sobre el costo, y fingir uno seria inventar un margen.
 */
export function markupDesdePrecio(entrada: {
  costo: number;
  costoMoneda: Moneda;
  /** El precio que se quiere lograr, en `precioMoneda`. */
  precioDeseado: number;
  precioMoneda: Moneda;
  markupTipo: MarkupTipo;
  /** Requerida cuando `markupTipo` es FIJO: en que moneda queda el monto. */
  markupMoneda?: Moneda;
  cotizacion: number;
}): number | null {
  const { costo, costoMoneda, precioDeseado, precioMoneda, markupTipo, cotizacion } = entrada;
  if (!(costo > 0)) return null;

  const base = convertir(costo, costoMoneda, precioMoneda, cotizacion);
  if (!(base > 0)) return null;

  const diferencia = precioDeseado - base;

  if (markupTipo === "PORCENTAJE") {
    return redondear((diferencia / base) * 100);
  }
  const moneda = entrada.markupMoneda ?? precioMoneda;
  return redondear(convertir(diferencia, precioMoneda, moneda, cotizacion));
}

/**
 * El margen Y el redondeo que reproducen un precio escrito.
 *
 * `markupDesdePrecio` devuelve el porcentaje con dos decimales, que es lo que
 * guarda la base, y con dos decimales la vuelta no cierra: US$ 245 a US$ 350
 * es 42,857… %, se guarda 42,86 % y el precio que sale es US$ 350,01. Asi
 * quedaron 113 productos con centavos en el precio.
 *
 * Lo que cierra la vuelta es tomar el margen JUSTO por debajo y dejar que el
 * redondeo —que siempre sube— lo lleve al numero escrito. Si el producto ya
 * tiene un redondeo se prueba con ese; si estaba sin redondear y el precio es
 * entero, se prueba al entero y, en pesos, a la terminacion en 990 o al cien
 * (en $ 55.000 un centesimo de margen son $ 5,50: el entero no alcanza).
 * Cuando nada lo reproduce se devuelve el margen de siempre con el redondeo
 * que tenia: no se inventa un redondeo que cambie el precio.
 *
 * La excepcion es el entero con un precio con centavos. El entero lo pone este
 * mismo despeje al escribir un precio redondo, y despues era el unico que se
 * probaba: escribir US$ 349,50 guardaba US$ 350 sin decir nada. Un precio con
 * centavos pide sin redondear, que es lo que habia antes del entero.
 *
 * null en los mismos casos que `markupDesdePrecio`.
 */
export function despejarPrecio(entrada: {
  costo: number;
  costoMoneda: Moneda;
  precioDeseado: number;
  precioMoneda: Moneda;
  markupTipo: MarkupTipo;
  markupMoneda?: Moneda;
  redondeo: Redondeo;
  cotizacion: number;
}): { markupValor: number; redondeo: Redondeo } | null {
  const markup = markupDesdePrecio(entrada);
  if (markup === null) return null;

  const objetivo = redondear(entrada.precioDeseado);
  const calcular = (markupValor: number, redondeo: Redondeo) =>
    calcularPrecio({
      costo: entrada.costo,
      costoMoneda: entrada.costoMoneda,
      markupTipo: entrada.markupTipo,
      markupValor,
      markupMoneda: entrada.markupMoneda ?? entrada.precioMoneda,
      precioMoneda: entrada.precioMoneda,
      redondeo,
      cotizacion: entrada.cotizacion,
    });

  if (calcular(markup, entrada.redondeo).precioVenta === objetivo) {
    return { markupValor: markup, redondeo: entrada.redondeo };
  }

  const centavosSobreEntero = entrada.redondeo === "UNIDAD" && !Number.isInteger(objetivo);
  const candidatos: Redondeo[] = centavosSobreEntero
    ? ["NINGUNO"]
    : entrada.redondeo !== "NINGUNO"
      ? [entrada.redondeo]
      : Number.isInteger(objetivo)
        ? [
            "UNIDAD",
            ...(objetivo % 1000 === 990 ? (["TERMINACION_990"] as const) : []),
            ...(objetivo % 100 === 0 ? (["CIEN"] as const) : []),
          ]
        : [];

  // El margen que deja el precio sin redondear en el escrito o apenas abajo.
  // Un par de centesimos alcanzan: el despeje ya cae a menos de uno.
  let debajo = markup;
  for (let i = 0; i < 3 && calcular(debajo, "NINGUNO").precioBruto > objetivo; i++) {
    debajo = redondear(debajo - 0.01);
  }
  for (const redondeo of candidatos) {
    if (calcular(debajo, redondeo).precioVenta === objetivo) {
      return { markupValor: debajo, redondeo };
    }
  }
  // Sin redondear y con dos decimales de margen puede no cerrar al centavo (en
  // US$ 920 un centesimo de margen son 9 centavos): se queda el margen que mas
  // se acerca, que igual esta mucho mas cerca que el entero de arriba.
  if (centavosSobreEntero) {
    const lejos = (m: number) => Math.abs(calcular(m, "NINGUNO").precioVenta - objetivo);
    const cerca = [markup, debajo, redondear(markup + 0.01)].reduce((a, b) =>
      lejos(b) < lejos(a) ? b : a,
    );
    return { markupValor: cerca, redondeo: "NINGUNO" };
  }
  return { markupValor: markup, redondeo: entrada.redondeo };
}

export interface CalculoPrecioInput {
  costo: number;
  costoMoneda: Moneda;
  markupTipo: MarkupTipo;
  markupValor: number;
  /** Requerida cuando `markupTipo` es FIJO: en que moneda esta el monto fijo. */
  markupMoneda?: Moneda | null;
  /** Moneda en la que se comercializa el producto. */
  precioMoneda: Moneda;
  redondeo?: Redondeo;
  /** Si viene, pisa por completo el precio calculado. */
  precioManual?: number | null;
  cotizacion: number;
}

export interface CalculoPrecioResultado {
  /** Costo llevado a la moneda de venta. */
  costoBase: number;
  /** Lo que suma el markup, en la moneda de venta. */
  markupImporte: number;
  /** Precio antes del redondeo comercial. */
  precioBruto: number;
  /** Precio final de venta. */
  precioVenta: number;
  /** true si `precioManual` piso el calculo. */
  esManual: boolean;
}

/**
 * Motor de precios. Replica exactamente el trigger `trg_productos_precio`.
 *
 * El caso que justifica toda esta maquinaria: el proveedor cotiza el iPhone en
 * USD, el local lo vende en pesos, y el markup puede ser "25%" o "USD 50 fijos".
 * Las cuatro combinaciones de moneda tienen que dar el mismo numero aca y en la
 * base de datos.
 */
export function calcularPrecio(input: CalculoPrecioInput): CalculoPrecioResultado {
  const {
    costo,
    costoMoneda,
    markupTipo,
    markupValor,
    markupMoneda,
    precioMoneda,
    redondeo = "NINGUNO",
    precioManual,
    cotizacion,
  } = input;

  const costoBase = convertir(costo, costoMoneda, precioMoneda, cotizacion);

  let markupImporte: number;
  if (markupTipo === "PORCENTAJE") {
    markupImporte = redondear(costoBase * (markupValor / 100));
  } else {
    if (!markupMoneda) {
      throw new Error("calcularPrecio: markupTipo FIJO requiere markupMoneda.");
    }
    markupImporte = convertir(markupValor, markupMoneda, precioMoneda, cotizacion);
  }

  const precioBruto = redondear(costoBase + markupImporte);
  const precioRedondeado = aplicarRedondeo(precioBruto, redondeo);

  const esManual = precioManual !== null && precioManual !== undefined;

  return {
    costoBase,
    markupImporte,
    precioBruto,
    precioVenta: esManual ? redondear(precioManual) : precioRedondeado,
    esManual,
  };
}

/**
 * Margen de una venta: cuanto se gano sobre el costo congelado en el momento.
 * Ambos importes se llevan a `moneda` antes de restar — de lo contrario se
 * estarian restando peras y manzanas.
 */
export function calcularMargen(params: {
  precioUnitario: number;
  precioMoneda: Moneda;
  costoUnitario: number;
  costoMoneda: Moneda;
  cantidad: number;
  cotizacion: number;
  moneda: Moneda;
}): { ingreso: number; costo: number; ganancia: number; margenPct: number } {
  const { precioUnitario, precioMoneda, costoUnitario, costoMoneda, cantidad, cotizacion, moneda } =
    params;

  const ingreso = redondear(convertir(precioUnitario, precioMoneda, moneda, cotizacion) * cantidad);
  const costo = redondear(convertir(costoUnitario, costoMoneda, moneda, cotizacion) * cantidad);
  const ganancia = redondear(ingreso - costo);
  const margenPct = ingreso === 0 ? 0 : redondear((ganancia / ingreso) * 100);

  return { ingreso, costo, ganancia, margenPct };
}

/** Aplica un porcentaje a un costo. Usado por la actualizacion masiva de listas. */
export function aplicarPorcentaje(valor: number, pct: number): number {
  return redondear(valor * (1 + pct / 100));
}

// ─────────────────────────────────────────────────────────────────────────────
// Descuentos
// ─────────────────────────────────────────────────────────────────────────────

export const DESCUENTO_TIPOS = ["PORCENTAJE", "MONTO"] as const;
export type DescuentoTipo = (typeof DESCUENTO_TIPOS)[number];

/**
 * Descuento de comprobante. Nunca puede exceder el subtotal — un descuento que
 * deja el total en negativo es un error de carga, no una nota de credito.
 */
export function calcularDescuento(
  subtotal: number,
  tipo: DescuentoTipo,
  valor: number,
): { importe: number; total: number } {
  const bruto = tipo === "PORCENTAJE" ? subtotal * (valor / 100) : valor;
  const importe = redondear(Math.min(Math.max(bruto, 0), subtotal));
  return { importe, total: redondear(subtotal - importe) };
}

/** Linea de comprobante con su descuento propio. */
export function importeLinea(precioUnitario: number, cantidad: number, descuentoPct = 0): number {
  const bruto = precioUnitario * cantidad;
  const pct = Math.min(Math.max(descuentoPct, 0), 100);
  return redondear(bruto * (1 - pct / 100));
}

// ─────────────────────────────────────────────────────────────────────────────
// Formato
// ─────────────────────────────────────────────────────────────────────────────

const FORMATEADORES: Record<Moneda, Intl.NumberFormat> = {
  ARS: new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
  USD: new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
};

/**
 * Formatea un importe con su simbolo.
 *
 * `es-AR` produce "$ 1.234,56" para ARS y "US$ 1.234,56" para USD: los dos
 * simbolos son distinguibles a simple vista, que es exactamente lo que se
 * necesita en un sistema donde confundir monedas cuesta plata real.
 */
export function formatear(monto: number, moneda: Moneda): string {
  return FORMATEADORES[moneda].format(sinCeroNegativo(monto, DECIMALES_IMPORTE));
}

/** Formatea sin simbolo, para celdas de tabla que ya tienen la moneda en el encabezado. */
export function formatearNumero(monto: number, decimales = DECIMALES_IMPORTE): string {
  return new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  }).format(sinCeroNegativo(monto, decimales));
}

/**
 * Lo que redondeado da cero, se muestra como cero y sin signo.
 *
 * `Intl` formatea -0 como «-$ 0,00», y -0 aparece solo: negar un cero para
 * mostrar un gasto o un aporte (`-gastos`) da -0. Pasaba en Ganancias (once
 * «-$ 0,00» en la columna Gastos) y en Socios («-US$ 0,00» en Aportes). Lo
 * mismo con un -0,001 que se redondea a cero.
 */
function sinCeroNegativo(monto: number, decimales: number): number {
  return Math.abs(monto) < 0.5 / 10 ** decimales ? 0 : monto;
}

/**
 * La cotizacion como numero pelado: "1.601".
 *
 * Se guarda con cuatro decimales (`DECIMALES_COTIZACION`) porque asi la usa la
 * aritmetica, pero nadie del local piensa el dolar en diezmilesimos: en
 * pantalla va entero. Si alguna vez viene con centavos (una carga manual de
 * "1.601,50") se muestran, porque ocultarlos seria mentir sobre el numero que
 * rige.
 */
export function formatearCotizacionNumero(cotizacion: number): string {
  return new Intl.NumberFormat("es-AR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(cotizacion);
}

/** Formatea una cotizacion con su unidad: "1.601 ARS/USD". */
export function formatearCotizacion(cotizacion: number): string {
  return `${formatearCotizacionNumero(cotizacion)} ARS/USD`;
}

/**
 * Parsea un importe tipeado por un humano en formato argentino.
 *
 * EL CASO QUE OBLIGA A LA HEURISTICA: un argentino tipea un precio de ochenta y
 * nueve mil novecientos noventa pesos como "89.990". Interpretar ese punto como
 * decimal da 89,99 — un producto de $89.990 pasaria a costar noventa pesos, y el
 * sistema lo aceptaria sin chistar. Es el peor error posible en un campo de
 * plata: silencioso y de tres ordenes de magnitud.
 *
 * Reglas, en orden:
 *
 *   1. Hay coma  -> la coma es el decimal y los puntos son miles.
 *                   "1.234,56" -> 1234.56    "1234,56" -> 1234.56
 *   2. Varios puntos -> todos son separadores de miles.
 *                   "1.234.567" -> 1234567
 *   3. Un punto con EXACTAMENTE 3 digitos detras -> separador de miles.
 *                   "89.990" -> 89990        "1.234" -> 1234
 *   4. Un punto con otra cantidad de digitos -> decimal (formato anglosajon,
 *      que igual aparece cuando se pega un valor de otro sistema).
 *                   "89.99" -> 89.99         "1.5" -> 1.5
 *
 * La regla 3 tiene un falso positivo inevitable: quien quiera escribir
 * exactamente "1.000" pensando en un peso con tres decimales obtiene 1000. Es
 * aceptable — los importes se persisten con dos decimales, asi que un tercer
 * decimal no existe en este sistema, mientras que los miles aparecen en cada
 * precio del catalogo.
 *
 * Devuelve null si no es interpretable. Nunca un 0 silencioso: en un campo de
 * plata, un error visible es infinitamente mejor que un cero inventado.
 */
export function parsearImporte(texto: string): number | null {
  const limpio = texto.replace(/[^\d,.\-]/g, "").trim();
  if (limpio === "" || limpio === "-") return null;

  // Un signo menos solo vale adelante. "1-2" no es un numero.
  const negativo = limpio.startsWith("-");
  const cuerpo = limpio.replace(/-/g, "");
  if (cuerpo === "") return null;

  const puntos = (cuerpo.match(/\./g) ?? []).length;
  const comas = (cuerpo.match(/,/g) ?? []).length;

  let normalizado: string;

  if (comas > 0) {
    // Regla 1: la coma manda como decimal. Solo se admite una.
    if (comas > 1) return null;
    normalizado = cuerpo.replace(/\./g, "").replace(",", ".");
  } else if (puntos > 1) {
    // Regla 2: "1.234.567"
    normalizado = cuerpo.replace(/\./g, "");
  } else if (puntos === 1) {
    const [, decimales = ""] = cuerpo.split(".");
    // Regla 3 vs 4: tres digitos detras del punto es un separador de miles.
    normalizado = decimales.length === 3 ? cuerpo.replace(".", "") : cuerpo;
  } else {
    normalizado = cuerpo;
  }

  const valor = Number(normalizado);
  if (!Number.isFinite(valor)) return null;
  return redondear(negativo ? -valor : valor);
}

// ─────────────────────────────────────────────────────────────────────────────
// Banda de cordura de la cotizacion
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Los limites de lo que puede ser un dolar expresado en pesos.
 *
 * POR QUE EXISTE ESTO
 * La unica validacion de una cotizacion en todo el sistema era `> 0`, repetida
 * en veinte esquemas de zod. Con eso, `1.59` pasaba sin que nada chistara — y
 * 1,59 es exactamente lo que `Number("1.590")` devuelve cuando alguien escribe
 * el dolar como se escribe en Argentina. Un pago de $ 1.000.000 a ese "dolar"
 * cancelaba US$ 628.930 de deuda en vez de US$ 629.
 *
 * El parseo ya se arreglo (`parsearImporte`), pero un numero puede entrar por
 * otros caminos: la API a mano, un import, una pantalla nueva que se olvide.
 * Esta banda es la red de abajo.
 *
 * LOS NUMEROS
 * El piso de 100 es historia reciente: el dolar estuvo en $ 100 en 2019. El
 * techo de 100.000 deja lugar para sesenta veces el valor de hoy. Ninguno de
 * los dos pretende ser ajustado: pretenden que 1,59 y 15.900.000 no entren.
 *
 * LO QUE ESTO NO ES
 * No es una banda relativa a la cotizacion del dia. Esa seria mejor —atraparia
 * un 1590 escrito cuando el dolar esta en 1.200— pero necesita leer la base, y
 * esto tiene que poder usarse en un `z.number()` sincronico y en el navegador
 * antes de mandar nada. La banda relativa va donde la pantalla ya conoce la
 * cotizacion del sistema, como en `DialogPagoProveedor`.
 */
export const COTIZACION_MINIMA = 100;
export const COTIZACION_MAXIMA = 100_000;

/** Si un numero puede ser un dolar en pesos. Ver `COTIZACION_MINIMA`. */
export function cotizacionRazonable(valor: number): boolean {
  return Number.isFinite(valor) && valor >= COTIZACION_MINIMA && valor <= COTIZACION_MAXIMA;
}

/**
 * Cuanto se aparta una cotizacion pactada de la del sistema, en veces.
 *
 * Sirve para avisarle al usuario antes de confirmar: pactar por encima o por
 * debajo del dolar del sistema es normal y es justamente el negocio, pero
 * pactar a un tercio o al triple es casi siempre un dedazo.
 */
export function desvioCotizacion(pactada: number, delSistema: number): number {
  if (!(pactada > 0) || !(delSistema > 0)) return Infinity;
  return pactada > delSistema ? pactada / delSistema : delSistema / pactada;
}
